import { useState } from "react";
import { X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// ---------- localized copy ---------- //
const COPY = {
  de: {
    badge: "3D · FABRIK-KARTE",
    overline: "— Werkstour interaktiv",
    title_1: "Klicken Sie sich",
    title_2: "durch unsere Halle.",
    intro:
      "Ein isometrischer Grundriss unserer Produktion in Mels. Klicken Sie auf eine Station, um mehr über den Prozess zu erfahren.",
    close: "Schliessen",
    stations: {
      wareneingang: {
        name: "Wareneingang",
        info: "Zentrale Anlieferstelle. Jede Charge wird beim Eingang gewogen, dokumentiert und einer eindeutigen Auftragsnummer zugeordnet.",
      },
      vorbehandlung: {
        name: "Vorbehandlung",
        info: "Entfetten, Beizen, Spülen — die Oberfläche wird für den galvanischen Prozess vorbereitet. Ohne saubere Basis keine haltbare Schicht.",
      },
      galvanik: {
        name: "Galvanik-Halle",
        info: "Herz der Anlage. Verzinken, Zink-Nickel, chemisch Nickel und weitere Verfahren. Alle Bäder sind temperatur- und ampèregeregelt.",
      },
      trommel: {
        name: "Trommelanlage",
        info: "Für Kleinteile und Grossserien — die Werkstücke werden in perforierten Trommeln durch die Bäder gefahren.",
      },
      cns: {
        name: "CNS-Bereich",
        info: "Elektropolieren und Beizen für Edelstahl. Klinisch reine Oberflächen für Medizin, Lebensmittel und Pharma.",
      },
      labor: {
        name: "Labor & Prüfung",
        info: "Schichtdickenmessung, Salzsprühtest, Sichtkontrolle und Dokumentation. Nichts geht ohne Freigabe raus.",
      },
      versand: {
        name: "Versand",
        info: "Verpackung, Etikettierung, Übergabe an Spedition. 5–7 Werktage nach Wareneingang zurück beim Kunden.",
      },
      buero: {
        name: "Büro & Empfang",
        info: "Auftragsabwicklung, Kalkulation, Fachberatung. Hier arbeitet Marco Mandic mit dem Team.",
      },
    },
  },
  en: {
    badge: "3D · FLOOR PLAN",
    overline: "— Interactive tour",
    title_1: "Click through",
    title_2: "our shop floor.",
    intro:
      "An isometric floor plan of our facility in Mels. Click a station to learn more about the process.",
    close: "Close",
    stations: {
      wareneingang: { name: "Goods-In", info: "Central receiving. Every batch is weighed, documented and assigned a unique job number." },
      vorbehandlung: { name: "Pre-treatment", info: "Degrease, pickle, rinse — surfaces are prepared for the electroplating process." },
      galvanik: { name: "Plating hall", info: "Heart of the plant. Zinc, zinc-nickel, electroless nickel and more. All baths are temperature and current controlled." },
      trommel: { name: "Barrel line", info: "For small parts and high volumes — components run through the baths in perforated barrels." },
      cns: { name: "Stainless area", info: "Electropolishing and pickling for stainless. Ultra-clean surfaces for medical, food and pharma." },
      labor: { name: "Lab & QC", info: "Coating thickness, salt spray, visual inspection and documentation. Nothing ships without sign-off." },
      versand: { name: "Dispatch", info: "Packing, labelling, freight handover. 5–7 business days back to the customer." },
      buero: { name: "Office & reception", info: "Order intake, quoting, expert advice. Marco Mandic and the team work here." },
    },
  },
  fr: {
    badge: "3D · PLAN D'USINE",
    overline: "— Visite interactive",
    title_1: "Cliquez à travers",
    title_2: "notre atelier.",
    intro:
      "Un plan isométrique de notre site à Mels. Cliquez sur une station pour en apprendre davantage.",
    close: "Fermer",
    stations: {
      wareneingang: { name: "Réception", info: "Réception centrale. Chaque lot est pesé, documenté et associé à un numéro d'ordre unique." },
      vorbehandlung: { name: "Prétraitement", info: "Dégraissage, décapage, rinçage — les surfaces sont préparées au procédé galvanique." },
      galvanik: { name: "Halle galvanique", info: "Cœur de l'installation. Zingage, zinc-nickel, nickel chimique et plus. Bains à régulation de température et d'ampérage." },
      trommel: { name: "Ligne au tonneau", info: "Pour petites pièces et grandes séries — les pièces circulent dans des tonneaux perforés." },
      cns: { name: "Zone inox", info: "Électropolissage et décapage pour l'inox. Surfaces ultra-propres pour médical, alimentaire et pharma." },
      labor: { name: "Labo & CQ", info: "Épaisseur de couche, brouillard salin, contrôle visuel et documentation." },
      versand: { name: "Expédition", info: "Emballage, étiquetage, remise au transporteur. Retour chez le client sous 5 à 7 jours ouvrés." },
      buero: { name: "Bureau & accueil", info: "Prise d'ordre, devis, conseil expert. Marco Mandic et l'équipe y travaillent." },
    },
  },
  it: {
    badge: "3D · PIANTA STABILIMENTO",
    overline: "— Visita interattiva",
    title_1: "Cliccate attraverso",
    title_2: "il nostro reparto.",
    intro:
      "Una pianta isometrica del nostro sito a Mels. Clicchi su una stazione per saperne di più sul processo.",
    close: "Chiudi",
    stations: {
      wareneingang: { name: "Accettazione merci", info: "Ricezione centrale. Ogni lotto viene pesato, documentato e associato a un numero d'ordine univoco." },
      vorbehandlung: { name: "Pretrattamento", info: "Sgrassaggio, decapaggio, risciacquo — le superfici vengono preparate al processo galvanico." },
      galvanik: { name: "Reparto galvanico", info: "Cuore dell'impianto. Zincatura, zinco-nichel, nichel chimico e altro. Tutti i bagni sono a controllo di temperatura e corrente." },
      trommel: { name: "Linea a tamburo", info: "Per piccoli pezzi e grandi serie — i componenti passano attraverso i bagni in tamburi perforati." },
      cns: { name: "Area inox", info: "Elettropolitura e decapaggio per acciaio inox. Superfici ultra-pulite per medicale, alimentare e farmaceutico." },
      labor: { name: "Laboratorio & QC", info: "Spessore rivestimento, nebbia salina, controllo visivo e documentazione." },
      versand: { name: "Spedizione", info: "Imballo, etichettatura, consegna spedizioniere. Ritorno al cliente in 5–7 giorni lavorativi." },
      buero: { name: "Ufficio & accoglienza", info: "Accettazione ordini, calcolo, consulenza. Qui lavora Marco Mandic con il team." },
    },
  },
};

// Isometric station coordinates (in the SVG's own coordinate system, viewBox 0 0 1200 700)
// Each rect uses a hand-crafted isometric parallelogram path via <polygon>.
// Layout:
//   Row A (top):    Büro       Wareneingang   Vorbehandlung
//   Row B (middle): Trommel    Galvanik-Halle (WIDE)  CNS-Bereich
//   Row C (bottom): Labor      Versand
const STATIONS = [
  // { key, points, label anchor {x,y}, size }
  { key: "buero",         color: "#3b3b40", polygon: "80,180 260,140 300,180 120,220", label: { x: 190, y: 190 } },
  { key: "wareneingang",  color: "#4a4a4f", polygon: "320,150 500,110 540,150 360,190", label: { x: 430, y: 160 } },
  { key: "vorbehandlung", color: "#5a5a5f", polygon: "560,130 740,90  780,130 600,170", label: { x: 670, y: 140 } },

  { key: "trommel",       color: "#2f2f34", polygon: "80,300 260,260 300,300 120,340", label: { x: 190, y: 310 } },
  { key: "galvanik",      color: "#1e1e22", polygon: "320,280 780,180 820,220 360,320", label: { x: 570, y: 260 } },
  { key: "cns",           color: "#3d3d42", polygon: "840,200 1000,160 1040,200 880,240", label: { x: 940, y: 210 } },

  { key: "labor",         color: "#3d3d42", polygon: "80,420 260,380 300,420 120,460", label: { x: 190, y: 430 } },
  { key: "versand",       color: "#4a4a4f", polygon: "320,400 500,360 540,400 360,440", label: { x: 430, y: 410 } },
];

export const FabrikKarte = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const [selected, setSelected] = useState(null);

  const station = selected && c.stations[selected] ? { key: selected, ...c.stations[selected] } : null;

  return (
    <section
      id="fabrik-karte"
      data-testid="fabrik-karte-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/15 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" />
                {c.badge}
              </span>
              <span>{c.overline}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="reveal bg-[#121214] border border-white/10 relative overflow-hidden">
          {/* subtle iso grid */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
            backgroundImage: "linear-gradient(60deg, rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(-60deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }} />
          <svg
            viewBox="0 0 1200 560"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto relative"
            aria-label="Isometrischer Grundriss"
          >
            {/* factory hall outer shell */}
            <polygon
              points="30,500 40,150 780,50 1180,180 1170,540 400,550"
              fill="#08080A"
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="1"
            />
            <polygon
              points="30,500 40,150 780,50"
              fill="rgba(255,255,255,0.02)"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
            />
            <polygon
              points="780,50 1180,180 1170,540"
              fill="rgba(255,255,255,0.03)"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
            />

            {STATIONS.map((s) => {
              const isSelected = selected === s.key;
              return (
                <g key={s.key} data-testid={`station-${s.key}`} className="cursor-pointer" onClick={() => setSelected(s.key)}>
                  <polygon
                    points={s.polygon}
                    fill={isSelected ? "#ffffff" : s.color}
                    stroke={isSelected ? "#ffffff" : "rgba(255,255,255,0.18)"}
                    strokeWidth={isSelected ? "2" : "1"}
                    className="transition-all duration-300 hover:opacity-90"
                    style={{ filter: isSelected ? "drop-shadow(0 0 24px rgba(255,255,255,0.35))" : undefined }}
                  />
                  {/* pulsing marker */}
                  <circle
                    cx={s.label.x}
                    cy={s.label.y - 22}
                    r={isSelected ? "8" : "5"}
                    fill={isSelected ? "#000" : "#fff"}
                    className="pulse-dot"
                  />
                  <circle
                    cx={s.label.x}
                    cy={s.label.y - 22}
                    r="14"
                    fill="none"
                    stroke={isSelected ? "#000" : "#fff"}
                    strokeOpacity="0.25"
                    strokeWidth="1"
                  />
                  <text
                    x={s.label.x}
                    y={s.label.y + 4}
                    textAnchor="middle"
                    className={`font-medium ${isSelected ? "fill-black" : "fill-white"}`}
                    style={{ fontSize: 12, letterSpacing: 0.4 }}
                  >
                    {c.stations[s.key].name}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Station detail card */}
          {station && (
            <div
              data-testid="station-detail"
              className="absolute inset-x-4 md:inset-auto md:right-6 md:top-6 md:w-80 bg-black/80 backdrop-blur-md border border-white/15 p-5 text-white"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="overline mb-2">{c.overline.replace("— ", "")}</div>
                  <div className="font-display text-xl font-semibold tracking-tight mb-3">
                    {station.name}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  data-testid="station-close"
                  className="text-white/60 hover:text-white p-1"
                  aria-label={c.close}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                {station.info}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FabrikKarte;
